import React from 'react';
import { preventNonNumericValue, toNumberOrMinusOne } from 'utils/helpers';
import { Col, Form, FormInstance, InputNumber, Row, Tag, Typography } from 'components';
import { useShippingInformation } from './useShippingInformation';
import styles from './../styles.module.scss';

const { Text } = Typography;

interface IShippingInformation {
  handleFormItemBlur: (fieldKey: string, value: string | number, groupObj?: string) => void;
  handleFormItemChange: (fieldKey: string, value: string, groupObj?: string) => void;
  form: FormInstance;
  leadType: 'DAY' | 'WEEK';
}

export const ShippingInformation = React.memo((props: IShippingInformation) => {
  const { handleFormItemBlur, handleFormItemChange, leadType, form } = props;
  const { handleTypeChange, type } = useShippingInformation({ leadType });
  return (
    <Row style={{ padding: '20px' }} gutter={[0, 20]}>
      <Col span={24}>
        <Text style={{ color: '#05796B' }} className={'font-weight-800 font-size-16'}>
          Shipping Information
        </Text>
      </Col>
      <Col span={24}>
        <Row gutter={[12, 20]}>
          <Col span={12}>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item name={['shippingInformation', 'leadTimeMin']} label={'Lead Time'}>
                  <InputNumber<number>
                    controls={false}
                    size={'large'}
                    onBlur={(e) =>
                      handleFormItemBlur('leadTimeMin', toNumberOrMinusOne(e.target.value), 'shippingInformation')
                    }
                    onBeforeInput={(event: any) => preventNonNumericValue(event)}
                    style={{ width: '100%' }}
                    placeholder={'Min'}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['shippingInformation', 'leadTimeMax']}
                  label={<span style={{ display: 'none' }}></span>}
                >
                  <InputNumber<number>
                    controls={false}
                    size={'large'}
                    onBlur={(e) =>
                      handleFormItemBlur('leadTimeMax', toNumberOrMinusOne(e.target.value), 'shippingInformation')
                    }
                    onBeforeInput={(event: any) => preventNonNumericValue(event)}
                    style={{ width: '100%' }}
                    suffix={
                      <Form.Item
                        noStyle
                        name={['shippingInformation', 'leadTime']}
                        label={<span style={{ display: 'none' }}></span>}
                      >
                        <Row className={'gap-4'}>
                          <Tag
                            className={`${styles.tag} ${type === 'DAY' && styles.selected_tag}`}
                            onClick={(event) => {
                              form.setFieldValue(['shippingInformation', 'leadTime'], 'DAY');
                              handleFormItemChange('leadTime', 'DAY', 'shippingInformation');
                              handleTypeChange('DAY');
                              event.stopPropagation();
                            }}
                          >
                            Day
                          </Tag>
                          <Tag
                            className={`${styles.tag} ${type === 'WEEK' && styles.selected_tag}`}
                            onClick={(event) => {
                              form.setFieldValue(['shippingInformation', 'leadTime'], 'WEEK');
                              handleFormItemChange('leadTime', 'WEEK', 'shippingInformation');
                              handleTypeChange('WEEK');
                              event.stopPropagation();
                            }}
                          >
                            Week
                          </Tag>
                        </Row>
                      </Form.Item>
                    }
                    placeholder={'Max'}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <Form.Item
              name={['shippingInformation', 'totalWeight']}
              label={'Total Weight'}
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const num = parseFloat(value);
                    if (isNaN(num)) {
                      return Promise.reject(new Error('Total Weight must be a valid number.'));
                    }
                    if (num < 0) {
                      return Promise.reject(new Error('Total Weight must be a positive number.'));
                    }
                    if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                      return Promise.reject(new Error('Total Weight can have up to 2 decimal points.'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber<number>
                controls={false}
                size={'large'}
                onBlur={(e) =>
                  handleFormItemBlur('totalWeight', toNumberOrMinusOne(e.target.value), 'shippingInformation')
                }
                onBeforeInput={(event: any) => preventNonNumericValue(event)}
                style={{ width: '100%' }}
                suffix={<Text type={'secondary'}>kg</Text>}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={['shippingInformation', 'shippingCoast']}
              label={'Shipping Cost'}
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const num = parseFloat(value);
                    if (isNaN(num)) {
                      return Promise.reject(new Error('Shipping Cost must be a valid number.'));
                    }
                    if (num < 0) {
                      return Promise.reject(new Error('Shipping Cost must be a positive number.'));
                    }
                    if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                      return Promise.reject(new Error('Shipping Cost can have up to 2 decimal points.'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber<number>
                controls={false}
                size={'large'}
                onBlur={(e) =>
                  handleFormItemBlur('shippingCoast', toNumberOrMinusOne(e.target.value), 'shippingInformation')
                }
                onBeforeInput={(event: any) => preventNonNumericValue(event)}
                style={{ width: '100%' }}
                suffix={<Text type={'secondary'}>€</Text>}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={['shippingInformation', 'packagingCoast']}
              label={'Packaging Cost'}
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const num = parseFloat(value);
                    if (isNaN(num)) {
                      return Promise.reject(new Error('Packaging Cost must be a valid number.'));
                    }
                    if (num < 0) {
                      return Promise.reject(new Error('Packaging Cost must be a positive number.'));
                    }
                    if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                      return Promise.reject(new Error('Packaging Cost can have up to 2 decimal points.'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber<number>
                controls={false}
                size={'large'}
                onBlur={(e) =>
                  handleFormItemBlur('packagingCoast', toNumberOrMinusOne(e.target.value), 'shippingInformation')
                }
                onBeforeInput={(event: any) => preventNonNumericValue(event)}
                style={{ width: '100%' }}
                suffix={<Text type={'secondary'}>€</Text>}
              />
            </Form.Item>
          </Col>
        </Row>
      </Col>
    </Row>
  );
});
